import {
  INTRANET_ABSENCE_NOTE_END_BEFORE_START,
  INTRANET_ABSENCE_NOTE_MAX,
  INTRANET_ABSENCE_NOTE_TIME_REQUIRED,
  INTRANET_ABSENCE_NOTE_TOO_LONG,
  zonedDateTimeParts,
  type IntranetAbsenceNoteOwn,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ApiRequestError } from '@/lib/api';
import { useOwnProfile } from '@/platform/features/users/use-own-profile';
import {
  useIntranetAbsenceNote,
  useRemoveIntranetAbsenceNote,
  useSaveIntranetAbsenceNote,
} from '@/products/intranet/features/intranet/use-intranet-absence-note';

const INTRO =
  'Die Abwesenheitsnotiz wird anderen Personen im Einzelchat, im Gruppenchat und im Profil angezeigt, solange der Zeitraum gilt.';
const LOAD_ERROR = 'Die Abwesenheitsnotiz konnte nicht geladen werden.';
const SAVE_ERROR = 'Die Abwesenheitsnotiz konnte nicht gespeichert werden.';
const REMOVE_ERROR = 'Die Abwesenheitsnotiz konnte nicht entfernt werden.';

type FormState = {
  startDate: string;
  endDate: string;
  hasTime: boolean;
  startTime: string;
  endTime: string;
  body: string;
};

export function AbsenceNoteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const profile = useOwnProfile(open);
  const noteQuery = useIntranetAbsenceNote(open);
  const [pending, setPending] = useState(false);
  const timeZone = trimmedTimeZone(profile.data?.timezone);
  const today = isoToday(timeZone);
  const ready = open && noteQuery.isSuccess && !profile.isPending;

  function close() {
    if (pending) {
      return;
    }
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent
        onEscapeKeyDown={(event) => {
          if (pending) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          if (pending) {
            event.preventDefault();
          }
        }}
      >
        {ready ? (
          <AbsenceNoteForm
            key={noteKey(noteQuery.data.note, today)}
            note={noteQuery.data.note}
            today={today}
            pending={pending}
            setPending={setPending}
            onClose={onClose}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Abwesenheitsnotiz hinzufügen</DialogTitle>
              <DialogDescription>{INTRO}</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              {noteQuery.isError ? LOAD_ERROR : 'Notiz wird geladen …'}
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AbsenceNoteForm({
  note,
  today,
  pending,
  setPending,
  onClose,
}: {
  note: IntranetAbsenceNoteOwn | null;
  today: string;
  pending: boolean;
  setPending: (pending: boolean) => void;
  onClose: () => void;
}) {
  const saveNote = useSaveIntranetAbsenceNote();
  const removeNote = useRemoveIntranetAbsenceNote();
  const [form, setForm] = useState<FormState>(() => formFromNote(note, today));
  const [error, setError] = useState<string | null>(null);

  const rangeInvalid = endBeforeStart(form);
  const timeMissing = form.hasTime && (!form.startTime || !form.endTime);
  const saveDisabled = pending || rangeInvalid || timeMissing || !form.startDate || !form.endDate;
  const shownError =
    error ??
    (rangeInvalid ? INTRANET_ABSENCE_NOTE_END_BEFORE_START : null) ??
    (timeMissing ? INTRANET_ABSENCE_NOTE_TIME_REQUIRED : null);

  function update(patch: Partial<FormState>) {
    setForm((current) => ({ ...current, ...patch }));
    setError(null);
  }

  function toggleTime(checked: boolean) {
    setForm((current) => {
      if (checked && !current.startTime && !current.endTime) {
        return { ...current, hasTime: true, startTime: '09:00', endTime: '17:00' };
      }
      return { ...current, hasTime: checked };
    });
    setError(null);
  }

  async function save() {
    if (saveDisabled) {
      return;
    }
    setPending(true);
    try {
      await saveNote.mutateAsync({
        startDate: form.startDate,
        endDate: form.endDate,
        hasTime: form.hasTime,
        startTime: form.hasTime ? form.startTime : null,
        endTime: form.hasTime ? form.endTime : null,
        body: form.body,
      });
      toast.success('Abwesenheitsnotiz gespeichert.');
      onClose();
    } catch (caught) {
      setError(saveErrorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  async function remove() {
    if (pending || !note) {
      return;
    }
    setPending(true);
    try {
      await removeNote.mutateAsync();
      toast.success('Abwesenheitsnotiz entfernt.');
      onClose();
    } catch {
      setError(REMOVE_ERROR);
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {note ? 'Abwesenheitsnotiz bearbeiten' : 'Abwesenheitsnotiz hinzufügen'}
        </DialogTitle>
        <DialogDescription>{INTRO}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="absence-start-date">Startdatum</Label>
            <Input
              id="absence-start-date"
              type="date"
              required
              disabled={pending}
              value={form.startDate}
              onChange={(event) => update({ startDate: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="absence-end-date">Enddatum</Label>
            <Input
              id="absence-end-date"
              type="date"
              required
              disabled={pending}
              value={form.endDate}
              onChange={(event) => update({ endDate: event.target.value })}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="absence-time"
            checked={form.hasTime}
            disabled={pending}
            onCheckedChange={toggleTime}
          />
          <Label htmlFor="absence-time">Uhrzeit hinzufügen</Label>
        </div>
        {form.hasTime ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="absence-start-time">Startzeit</Label>
              <Input
                id="absence-start-time"
                type="time"
                required
                disabled={pending}
                value={form.startTime}
                onChange={(event) => update({ startTime: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="absence-end-time">Endzeit</Label>
              <Input
                id="absence-end-time"
                type="time"
                required
                disabled={pending}
                value={form.endTime}
                onChange={(event) => update({ endTime: event.target.value })}
              />
            </div>
          </div>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="absence-body" className="sr-only">
            Abwesenheitsnotiz (optional)
          </Label>
          <Textarea
            id="absence-body"
            rows={3}
            maxLength={INTRANET_ABSENCE_NOTE_MAX}
            disabled={pending}
            placeholder="Abwesenheitsnotiz (optional)"
            value={form.body}
            onChange={(event) => update({ body: event.target.value })}
          />
        </div>
      </div>
      {shownError ? (
        <p role="alert" className="text-sm text-destructive">
          {shownError}
        </p>
      ) : null}
      <DialogFooter className="sm:justify-between">
        {note ? (
          <Button
            type="button"
            variant="destructive"
            disabled={pending}
            onClick={() => void remove()}
          >
            Notiz entfernen
          </Button>
        ) : (
          <span />
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button type="button" variant="outline" disabled={pending} onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="button" disabled={saveDisabled} onClick={() => void save()}>
            Speichern
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

function trimmedTimeZone(value: string | undefined): string {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : 'Europe/Berlin';
}

function noteKey(note: IntranetAbsenceNoteOwn | null, today: string): string {
  if (!note) {
    return `empty:${today}`;
  }
  return [
    note.startDate,
    note.endDate,
    note.hasTime ? '1' : '0',
    note.startTime ?? '',
    note.endTime ?? '',
    note.body ?? '',
  ].join('|');
}

function formFromNote(note: IntranetAbsenceNoteOwn | null, today: string): FormState {
  if (!note) {
    return {
      startDate: today,
      endDate: today,
      hasTime: false,
      startTime: '',
      endTime: '',
      body: '',
    };
  }
  return {
    startDate: note.startDate,
    endDate: note.endDate,
    hasTime: note.hasTime,
    startTime: note.startTime ?? '',
    endTime: note.endTime ?? '',
    body: note.body ?? '',
  };
}

function endBeforeStart(form: FormState): boolean {
  if (!form.startDate || !form.endDate) {
    return false;
  }
  if (form.endDate < form.startDate) {
    return true;
  }
  if (!form.hasTime || form.endDate !== form.startDate || !form.startTime || !form.endTime) {
    return false;
  }
  return form.endTime < form.startTime;
}

function isoToday(timeZone: string): string {
  const parts = zonedDateTimeParts(new Date(), timeZone);
  const month = String(parts.month).padStart(2, '0');
  const day = String(parts.day).padStart(2, '0');
  return `${parts.year}-${month}-${day}`;
}

function saveErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const message = error.error.message;
    if (
      message === INTRANET_ABSENCE_NOTE_END_BEFORE_START ||
      message === INTRANET_ABSENCE_NOTE_TOO_LONG ||
      message === INTRANET_ABSENCE_NOTE_TIME_REQUIRED
    ) {
      return message;
    }
  }
  return SAVE_ERROR;
}
